/* Dealership settings (docs/decisions/dealership-settings.md): aging
   boundaries, the asking-vs-AI scale, APR and margin floor, per dealership. */
import React, {
    useCallback, useEffect, useId, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Button, ConfirmDialog } from '../ui';
import { setLeaveGuard } from '../../utils/leaveGuard';
import useDealershipSettingsAdmin from '../../hooks/useDealershipSettingsAdmin';
import { useDealershipSettings } from '../../contexts/settingsContext';
import { useOptionalData } from '../../contexts/DataContext';
import {
    DEFAULT_SCHEMA, DEFAULT_VALUES, SETTING_KEYS, buildAgingBuckets, formatSettingTime,
} from '../../utils/dealershipSettings';
import { settingsPreview } from '../../utils/settingsPreview';
import { Alert, Note, SectionHead, Spinner } from './bits';
import { LockIcon } from './icons';

export const SETTINGS_GROUPS = Object.freeze([
    {
        id: 'aging',
        title: 'Aging',
        note: 'Inventory always has four aging buckets; you choose where each one ends.',
        keys: [SETTING_KEYS.agingBoundaries, SETTING_KEYS.criticalDays],
    },
    {
        id: 'pricing',
        title: 'Pricing vs AI estimate',
        note: 'How far the asking price may sit from the AI estimate before the chips and signals react.',
        keys: [SETTING_KEYS.gapNeutral, SETTING_KEYS.gapWarn, SETTING_KEYS.gapCritical, SETTING_KEYS.peerOverpriced],
    },
    {
        id: 'costs',
        title: 'Costs & margin',
        note: 'Used for floor plan carrying cost, margin after carrying cost and the margin colours. Users may set a personal margin floor within the allowed range.',
        keys: [SETTING_KEYS.apr, SETTING_KEYS.marginFloor, SETTING_KEYS.userFloorMin, SETTING_KEYS.userFloorMax],
    },
]);

const BOUNDARY_LABELS = ['Bucket 1 ends at', 'Bucket 2 ends at', 'Bucket 3 ends at'];

const fieldId = (base, key, i = null) => `${base}-${key.replace(/\./g, '-')}${i === null ? '' : `-${i}`}`;

/** "5 %", "30, 90, 180 days". */
export const formatSettingValue = (value, unit) => {
    if (value === null || value === undefined) return 'default';
    const body = Array.isArray(value) ? value.join(', ') : String(value);
    if (unit === '%') return `${body} %`;
    return unit ? `${body} ${unit}` : body;
};

const sameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

function SettingField({
    def, form, readOnly, baseId,
}) {
    const { key } = def;
    const value = form.draft[key];
    const parsed = form.parsed[key];
    const defaultValue = form.server.defaults[key] ?? DEFAULT_VALUES[key];
    const error = form.errors[key];
    const overrides = !sameValue(parsed, defaultValue);
    const descId = fieldId(baseId, key, 'desc');
    const errId = fieldId(baseId, key, 'err');
    const describedBy = [descId, error ? errId : null].filter(Boolean).join(' ');
    const unitText = def.unit === '%' ? '%' : def.unit;

    const meta = (
        <span className="ad2-set-meta">
            <span className="ad2-set-def">Default: {formatSettingValue(defaultValue, def.unit)}</span>
            {overrides && <span className="ad2-set-ovr">Overrides the default</span>}
            {overrides && !readOnly && (
                <button
                    type="button"
                    className="ad2-linkbtn"
                    onClick={() => {
                        form.resetField(key);
                        // The button goes once the value is the default: keep focus on the field (S-6).
                        document.getElementById(fieldId(baseId, key, Array.isArray(value) ? 0 : null))?.focus();
                    }}
                    aria-label={`Reset ${def.label} to the default`}
                >
                    Reset
                </button>
            )}
        </span>
    );

    if (Array.isArray(value)) {
        const buckets = Array.isArray(parsed) && parsed.every((n) => typeof n === 'number') && !error
            ? buildAgingBuckets(parsed) : null;
        return (
            <fieldset className={`ad2-set-f${error ? ' is-invalid' : ''}`} data-key={key}>
                <legend className="ad2-set-l">{def.label}</legend>
                <div className="ad2-set-bounds">
                    {value.map((v, i) => (
                        <div key={BOUNDARY_LABELS[i]} className="ad2-set-b">
                            <label className="ad2-set-sub" htmlFor={fieldId(baseId, key, i)}>{BOUNDARY_LABELS[i]}</label>
                            <span className="ad2-set-in">
                                <input
                                    id={fieldId(baseId, key, i)}
                                    className="v2-input"
                                    type="number"
                                    inputMode="numeric"
                                    min={def.min}
                                    max={def.max}
                                    step={1}
                                    value={v}
                                    readOnly={readOnly}
                                    aria-invalid={error ? 'true' : undefined}
                                    aria-describedby={describedBy}
                                    onChange={(e) => form.setField(key, e.target.value, i)}
                                />
                                <span className="ad2-set-u" aria-hidden="true">{unitText}</span>
                            </span>
                        </div>
                    ))}
                </div>
                <p id={descId} className="ad2-hint">
                    {def.description}
                    {buckets && <> Buckets: <strong>{buckets.map((b) => b.label).join(' · ')}</strong>.</>}
                </p>
                {meta}
                {error && <p id={errId} className="ad2-set-err">{error}</p>}
            </fieldset>
        );
    }

    return (
        <div className={`ad2-set-f${error ? ' is-invalid' : ''}`} data-key={key}>
            <label className="ad2-set-l" htmlFor={fieldId(baseId, key)}>{def.label}</label>
            <span className="ad2-set-in">
                <input
                    id={fieldId(baseId, key)}
                    className="v2-input"
                    type="number"
                    inputMode={def.type === 'int' ? 'numeric' : 'decimal'}
                    min={def.min}
                    max={def.max}
                    step={def.type === 'int' ? 1 : 'any'}
                    value={value}
                    readOnly={readOnly}
                    aria-invalid={error ? 'true' : undefined}
                    aria-describedby={describedBy}
                    onChange={(e) => form.setField(key, e.target.value)}
                />
                <span className="ad2-set-u" aria-hidden="true">{unitText}</span>
            </span>
            <p id={descId} className="ad2-hint">{def.description}</p>
            {meta}
            {error && <p id={errId} className="ad2-set-err">{error}</p>}
        </div>
    );
}

SettingField.propTypes = {
    def: PropTypes.object.isRequired,
    form: PropTypes.object.isRequired,
    readOnly: PropTypes.bool,
    baseId: PropTypes.string.isRequired,
};

/** Live preview: the loaded inventory under the edited values. */
export function SettingsPreview({ units, form }) {
    const errs = form.errors;
    const agingValid = !errs[SETTING_KEYS.agingBoundaries];
    const gapValid = !errs[SETTING_KEYS.gapNeutral] && !errs[SETTING_KEYS.gapWarn] && !errs[SETTING_KEYS.gapCritical];
    const now = useMemo(() => settingsPreview(units, form.server.values), [units, form.server.values]);
    const next = useMemo(
        () => settingsPreview(units, form.parsed, { agingValid, gapValid }),
        [units, form.parsed, agingValid, gapValid],
    );
    const gapRows = [
        { id: 'inline', label: 'In line', tone: 'sage' },
        { id: 'above', label: 'Above', tone: 'amber' },
        { id: 'wellAbove', label: 'Well above', tone: 'red' },
        { id: 'below', label: 'Below', tone: 'teal' },
    ];
    const delta = (a, b) => (a === b ? null : <span className="ad2-set-was">now {a.toLocaleString('en-US')}</span>);
    return (
        <section className="v2-board ad2-board ad2-set-pv" aria-labelledby="ad2-set-pv-h">
            <div className="ad2-set-pv-head">
                <h2 id="ad2-set-pv-h" className="ad2-h2">Preview</h2>
                <p className="ad2-sub">
                    {next.total.toLocaleString('en-US')} units in the current inventory, with the values above.
                    Nothing changes until you save.
                </p>
            </div>
            <div className="ad2-set-pv-grid" aria-live="polite">
                <div>
                    <h3 className="ad2-set-pv-t">Aging buckets</h3>
                    {next.aging ? (
                        <ul className="ad2-set-pv-list" data-testid="preview-aging">
                            {next.aging.map((b, i) => (
                                <li key={b.id}>
                                    <i className={`ad2-set-dot v2-tone-${b.tone}`} aria-hidden="true" />
                                    <span>{b.label}</span>
                                    <b>{b.count.toLocaleString('en-US')}</b>
                                    {delta(now.aging[i].count, b.count)}
                                </li>
                            ))}
                        </ul>
                    ) : <p className="ad2-hint">Fix the aging boundaries to see the buckets.</p>}
                </div>
                <div>
                    <h3 className="ad2-set-pv-t">Asking vs AI estimate</h3>
                    {next.gap ? (
                        <ul className="ad2-set-pv-list" data-testid="preview-gap">
                            {gapRows.map((r) => (
                                <li key={r.id}>
                                    <i className={`ad2-set-dot v2-tone-${r.tone}`} aria-hidden="true" />
                                    <span>{r.label}</span>
                                    <b>{next.gap[r.id].toLocaleString('en-US')}</b>
                                    {delta(now.gap[r.id], next.gap[r.id])}
                                </li>
                            ))}
                        </ul>
                    ) : <p className="ad2-hint">Fix the pricing thresholds to see the split.</p>}
                    {next.gap && <p className="ad2-hint">Used units with both an asking price and an AI price ({next.gap.priced.toLocaleString('en-US')}).</p>}
                </div>
            </div>
        </section>
    );
}

SettingsPreview.propTypes = { units: PropTypes.array.isRequired, form: PropTypes.object.isRequired };

export function SettingsHistory({ history, schema }) {
    const labelOf = (key) => (schema.find((s) => s.key === key) || DEFAULT_SCHEMA.find((s) => s.key === key))?.label || key;
    const unitOf = (key) => (schema.find((s) => s.key === key) || DEFAULT_SCHEMA.find((s) => s.key === key))?.unit;
    return (
        <section className="v2-board ad2-board" aria-labelledby="ad2-set-hist-h">
            <div className="ad2-set-pv-head">
                <h2 id="ad2-set-hist-h" className="ad2-h2">History</h2>
                <p className="ad2-sub">The last 20 changes.</p>
            </div>
            {history.length === 0 ? (
                <div className="ad2-empty"><p>No changes yet. Every value is the default.</p></div>
            ) : (
                <div className="v2-tscroll">
                    <table className="v2-table" aria-label="Settings history">
                        <thead>
                            <tr>
                                <th scope="col"><span>When</span></th>
                                <th scope="col"><span>Who</span></th>
                                <th scope="col"><span>Setting</span></th>
                                <th scope="col"><span>Change</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((h, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <tr key={`${h.changed_at}-${h.key}-${i}`}>
                                    <td>{formatSettingTime(h.changed_at)}</td>
                                    <td>{h.changed_by || '—'}</td>
                                    <td>{labelOf(h.key)}</td>
                                    <td>
                                        {formatSettingValue(h.old_value, unitOf(h.key))}
                                        {' → '}
                                        {formatSettingValue(h.new_value, unitOf(h.key))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

SettingsHistory.propTypes = { history: PropTypes.array.isRequired, schema: PropTypes.array.isRequired };

/**
 * The admin section. `dealers`: the dealerships the caller may pick (super
 * admins pick any; a dealer admin gets their own). The preview needs the
 * inventory of the dealership being edited, which is the one in view.
 */
export default function SettingsSection({ dealers, isSuper, onToast }) {
    const baseId = `ad2set${useId().replace(/:/g, '')}`;
    const current = useDealershipSettings();
    const data = useOptionalData();
    const firstId = dealers.some((d) => d.id === current.orgId) ? current.orgId : (dealers[0]?.id || current.orgId || null);
    const [orgId, setOrgId] = useState(firstId);
    useEffect(() => {
        if (!orgId && firstId) setOrgId(firstId);
    }, [orgId, firstId]);

    const inView = Boolean(orgId) && orgId === current.orgId;
    const onSaved = useCallback((savedOrg) => {
        if (savedOrg !== current.orgId) return;
        // The cache was purged server-side. The new settings apply together
        // with the rebuilt inventory (its signals are computed server-side),
        // so chips and signal counts never disagree (QA S-8).
        if (data?.refreshAll) current.applyAfterRefresh(() => data.refreshAll());
        else current.reload();
    }, [current, data]);
    const form = useDealershipSettingsAdmin(orgId, { onSaved });
    const readOnly = !form.canEdit;
    const schema = form.server.schema;
    const defOf = (key) => schema.find((s) => s.key === key) || DEFAULT_SCHEMA.find((s) => s.key === key);

    // The preview reads the inventory already in the app (loaded if needed).
    useEffect(() => {
        if (inView && data?.loadInventory && !data.inventoryData) data.loadInventory();
    }, [inView]); // eslint-disable-line react-hooks/exhaustive-deps
    const units = data?.inventoryData?.units || [];
    // S-9: no "0 units" while the inventory is still on its way.
    let previewState = 'ready';
    if (!data?.inventoryData) previewState = data?.inventoryError ? 'error' : 'loading';

    const alertRef = useRef(null);
    const statusRef = useRef(null);
    const titleRef = useRef(null);
    const saveRef = useRef(null);
    const reloadRef = useRef(null);
    const discardRef = useRef(null);
    const [confirmReload, setConfirmReload] = useState(false);
    const [focusSave, setFocusSave] = useState(false);
    const [focusTitle, setFocusTitle] = useState(false);
    useEffect(() => {
        if (focusTitle && !form.saveError) { titleRef.current?.focus(); setFocusTitle(false); }
    }, [focusTitle, form.saveError]);
    useEffect(() => {
        if (focusSave && form.dirty) { saveRef.current?.focus(); setFocusSave(false); }
    }, [focusSave, form.dirty]);
    useEffect(() => {
        if (confirmReload) discardRef.current?.focus();
    }, [confirmReload]);
    useEffect(() => { setConfirmReload(false); }, [form.saveError]);

    // 409: load the server's values; with unsaved edits, only after an inline
    // confirmation (S-6). Focus then goes to the heading.
    const doReload = async () => {
        setConfirmReload(false);
        await form.reload();
        setFocusTitle(true);
    };
    const askReload = () => {
        if (form.dirty) setConfirmReload(true);
        else doReload();
    };
    const resetAll = () => {
        form.resetAll();
        setFocusSave(true);
    };
    useEffect(() => {
        if (form.saveError) alertRef.current?.focus();
    }, [form.saveError]);
    useEffect(() => {
        if (form.savedAt) statusRef.current?.focus();
    }, [form.savedAt]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.valid) {
            const firstKey = Object.keys(DEFAULT_VALUES).find((k) => form.errors[k]);
            const target = document.querySelector(`[data-key="${firstKey}"] input`);
            target?.focus();
            return;
        }
        const ok = await form.save();
        if (ok && onToast) onToast('Dealership settings saved');
    };

    // Unsaved edits: leaving the section (or the console, or picking another
    // dealership) asks in the page first, never window.confirm (QA P2-6).
    const [leaveAsk, setLeaveAsk] = useState(null);
    const askLeave = useCallback(() => new Promise((resolve) => { setLeaveAsk({ resolve }); }), []);
    const guarded = form.dirty && !readOnly;
    useEffect(() => (guarded ? setLeaveGuard(askLeave) : undefined), [guarded, askLeave]);
    useEffect(() => {
        if (!guarded) return undefined;
        const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', onUnload);
        return () => window.removeEventListener('beforeunload', onUnload);
    }, [guarded]);
    const answerLeave = (leave) => {
        const ask = leaveAsk;
        setLeaveAsk(null);
        ask?.resolve(leave);
    };
    const pickDealer = async (next) => {
        if (guarded && !(await askLeave())) return;
        setOrgId(next);
    };

    const dealerName = dealers.find((d) => d.id === orgId)?.name;

    return (
        <>
            <SectionHead eyebrow={dealerName || 'Dealership'} title="Dealership settings" titleRef={titleRef}>
                The shared definitions this dealership works with: when a unit counts as aged, when an asking price
                is above the AI estimate, and the costs behind carrying cost and margin. Changes apply to everyone at
                the dealership.
            </SectionHead>

            {isSuper && dealers.length > 0 && (
                <label className="ad2-fld ad2-set-pick" htmlFor={`${baseId}-org`}>
                    <span>Dealership</span>
                    <select
                        id={`${baseId}-org`}
                        className="v2-input"
                        value={orgId || ''}
                        onChange={(e) => pickDealer(e.target.value)}
                    >
                        {dealers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                </label>
            )}

            {!orgId && <Note tone="warn">No dealership to configure.</Note>}
            {form.loadError && <Alert>{form.loadError}</Alert>}
            {form.loading && !form.loaded && (
                <div className="ad2-loading" role="status"><Spinner /> Loading settings…</div>
            )}

            {form.loaded && (
                <>
                    {readOnly && (
                        <Note>
                            <LockIcon /> You can view these settings. Only an admin of this dealership or Fusable staff can change them.
                        </Note>
                    )}

                    <form className="ad2-set-form" onSubmit={submit} noValidate aria-label="Dealership settings">
                        {SETTINGS_GROUPS.map((g) => (
                            <section key={g.id} className="v2-card ad2-set-group" aria-labelledby={`${baseId}-${g.id}`}>
                                <h2 id={`${baseId}-${g.id}`} className="ad2-h2">{g.title}</h2>
                                <p className="ad2-sub">{g.note}</p>
                                <div className="ad2-set-fields">
                                    {g.keys.map((key) => (
                                        <SettingField key={key} def={defOf(key)} form={form} readOnly={readOnly} baseId={baseId} />
                                    ))}
                                </div>
                            </section>
                        ))}

                        {form.saveError && (
                            <div className="ad2-alert ad2-set-alert" role="alert" tabIndex={-1} ref={alertRef}>
                                <span>{form.saveError}</span>
                                {form.conflict && !confirmReload && (
                                    <Button size="sm" onClick={askReload} ref={reloadRef}>Reload</Button>
                                )}
                                {form.conflict && confirmReload && (
                                    <span className="ad2-set-confirm" role="group" aria-label="Discard your unsaved changes?">
                                        <span>Discard your unsaved changes?</span>
                                        <Button size="sm" variant="danger" onClick={doReload} ref={discardRef}>Discard and reload</Button>
                                        <Button
                                            size="sm"
                                            onClick={() => {
                                                setConfirmReload(false);
                                                setTimeout(() => reloadRef.current?.focus(), 0);
                                            }}
                                        >
                                            Keep editing
                                        </Button>
                                    </span>
                                )}
                            </div>
                        )}
                        {form.savedAt && !form.dirty && (
                            <p className="ad2-set-saved" role="status" tabIndex={-1} ref={statusRef}>
                                Saved. The inventory and branch figures use the new values from now on.
                            </p>
                        )}

                        {!readOnly && (
                            <div className="ad2-set-acts">
                                <span className="ad2-hint">
                                    {form.dirty
                                        ? `${form.changedKeys.length} unsaved ${form.changedKeys.length === 1 ? 'change' : 'changes'}`
                                        : 'No unsaved changes'}
                                </span>
                                {form.anyNonDefault && (
                                    <Button onClick={resetAll} disabled={form.saving}>Reset all to defaults</Button>
                                )}
                                <Button onClick={form.discard} disabled={!form.dirty || form.saving}>Discard</Button>
                                <Button variant="primary" type="submit" disabled={!form.dirty || form.saving} ref={saveRef}>
                                    {form.saving ? <><Spinner />Saving…</> : 'Save'}
                                </Button>
                            </div>
                        )}
                    </form>

                    {inView && previewState === 'loading' && (
                        <div className="v2-board ad2-board ad2-set-pv">
                            <div className="ad2-loading" role="status"><Spinner /> Loading the inventory for the preview…</div>
                        </div>
                    )}
                    {inView && previewState === 'error' && (
                        <Note tone="warn">The inventory could not be loaded, so there is no preview. Saving still works.</Note>
                    )}
                    {inView && previewState === 'ready' && <SettingsPreview units={units} form={form} />}
                    {!inView && (
                        <Note>The live preview uses the inventory in view; open this dealership to preview against its units.</Note>
                    )}

                    <SettingsHistory history={form.history} schema={schema} />
                </>
            )}

            <ConfirmDialog
                open={Boolean(leaveAsk)}
                title="Discard unsaved changes?"
                message={`You have ${form.changedKeys.length} unsaved ${form.changedKeys.length === 1 ? 'change' : 'changes'} to the dealership settings. Leaving discards ${form.changedKeys.length === 1 ? 'it' : 'them'}.`}
                cancelLabel="Keep editing"
                confirmLabel="Discard"
                danger
                onConfirm={() => answerLeave(true)}
                onCancel={() => answerLeave(false)}
            />
        </>
    );
}

SettingsSection.propTypes = {
    dealers: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })).isRequired,
    isSuper: PropTypes.bool,
    onToast: PropTypes.func,
};
